import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  confirm,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
  useDebounceCallback,
} from "@devhop/ui";
import {
  RiAddLine,
  RiArrowDownLine,
  RiArrowLeftLine,
  RiArrowUpLine,
  RiCheckLine,
  RiDeleteBinLine,
  RiDragMove2Line,
  RiLiveLine,
  RiNewspaperLine,
  RiSaveLine,
} from "@remixicon/react";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Link,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { CATALOG_PAGE_LIMIT } from "@/modules/cf-feed/presentation/api/queries";
import type { AdminCatalogItem } from "@/modules/cf-feed/presentation/api/types";
import { updateCatalogSelection } from "@/modules/cf-feed/presentation/lib/catalog-add";
import { useFacebookPagesQuery } from "@/modules/facebook/presentation/api/queries";
import { FacebookPageCombobox } from "@/modules/facebook/presentation/ui/FacebookPageCombobox";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { QueryState } from "@/shared/ui/QueryState";
import type { PriceInput } from "../../domain/contracts/live-workspace";
import { productSetApi } from "../api/product-set-client";
import { WorkspaceCatalogCards } from "../ui/WorkspaceCatalogCards";
import { WorkspaceCatalogPriceDialog } from "../ui/WorkspaceCatalogPriceDialog";
import {
  WorkspaceProductCard,
  type WorkspaceProductCardAction,
} from "../ui/WorkspaceProductCard";
import { kip, WorkspaceSelect } from "../ui/WorkspaceProductEditor";

const listKey = (pageId: string) => ["cf-product-sets", pageId] as const;
const emptyPrice: PriceInput = { mode: "inherit" };

function productSetPriceFingerprint(
  price: PriceInput,
  items: AdminCatalogItem[],
) {
  return JSON.stringify([
    price,
    items
      .map((item) => [item.variantId, Number(item.price)])
      .sort(([left], [right]) => String(left).localeCompare(String(right))),
  ]);
}

function priceSummary(price: PriceInput) {
  if (price.mode === "inherit") return "ໃຊ້ລາຄາສິນຄ້າ";
  if (price.mode === "none") return "ບໍ່ລະບຸລາຄາ";
  if (price.mode === "percent") return `ຫຼຸດ ${price.value ?? 0}%`;
  if (price.mode === "fixed") return `ຫຼຸດ ${price.value ?? 0} ກີບ`;
  return `${price.value ?? 0} ກີບ`;
}

function PagePicker({
  value,
  onChange,
}: {
  value?: string;
  onChange: (value: string) => void;
}) {
  const pages = useFacebookPagesQuery();
  return (
    <div className="max-w-xl space-y-1">
      <p className="text-muted-foreground text-xs">ເພຈ Facebook</p>
      <FacebookPageCombobox
        pages={
          pages.data?.pages.filter((page) => page.status !== "disconnected") ??
          []
        }
        value={value ?? ""}
        onValueChange={onChange}
        placeholder="ເລືອກເພຈທີ່ຈະຈັດການຊຸດສິນຄ້າ"
        disabled={pages.isLoading}
        queryKey={["cf-product-set-pages"]}
      />
    </div>
  );
}

export function ProductSetsPage() {
  const { has } = usePermissions();
  const canManage = has("live:manage");
  const search = useSearch({ from: "/app/product-sets" });
  const nav = useNavigate({ from: "/app/product-sets" });
  const pageId = search.pageId;
  const sets = useQuery({
    queryKey: listKey(pageId ?? ""),
    queryFn: () => productSetApi.list(pageId ?? ""),
    enabled: Boolean(pageId),
  });
  return (
    <>
      <Header />
      <Main>
        <div className="mx-auto max-w-5xl space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="font-semibold text-2xl">ຊຸດສິນຄ້າ</h1>
              <p className="text-muted-foreground text-sm">
                ຕັ້ງ SKU ແລະລາຄາໄວ້ ແລ້ວນຳໄປໃຊ້ກັບ Feed ຫຼື Live
              </p>
            </div>
            <Button
              disabled={!pageId || !canManage}
              onClick={() =>
                nav({
                  to: "/app/product-sets/$id",
                  params: { id: "new" },
                  search: { pageId },
                })
              }
            >
              <RiAddLine size={18} /> ສ້າງຊຸດສິນຄ້າ
            </Button>
          </div>
          <PagePicker
            value={pageId}
            onChange={(next) => nav({ search: { pageId: next } })}
          />
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-56">ຊື່ຊຸດສິນຄ້າ</TableHead>
                  <TableHead className="text-right">SKU</TableHead>
                  <TableHead className="min-w-40">ລາຄາ</TableHead>
                  <TableHead className="hidden min-w-40 lg:table-cell">
                    ແກ້ໄຂລ່າສຸດ
                  </TableHead>
                  <TableHead className="text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {!pageId ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="h-24 text-center text-muted-foreground"
                    >
                      ເລືອກເພຈກ່ອນເພື່ອເບິ່ງ ຫຼື ສ້າງຊຸດສິນຄ້າ
                    </TableCell>
                  </TableRow>
                ) : sets.isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="h-24 text-center text-muted-foreground"
                    >
                      ກຳລັງໂຫຼດຊຸດສິນຄ້າ…
                    </TableCell>
                  </TableRow>
                ) : sets.isError ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center">
                      <Button
                        variant="outline"
                        onClick={() => void sets.refetch()}
                      >
                        ລອງໃໝ່
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : sets.data?.length ? (
                  sets.data.map((set) => (
                    <TableRow key={set.id}>
                      <TableCell className="font-medium">{set.name}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="secondary">{set.items.length}</Badge>
                      </TableCell>
                      <TableCell>
                        {priceSummary(set.items[0]?.price ?? emptyPrice)}
                      </TableCell>
                      <TableCell className="hidden text-muted-foreground text-sm lg:table-cell">
                        {formatDateTimeLocal(set.updatedAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button asChild size="sm" variant="outline">
                          <Link
                            to="/app/product-sets/$id"
                            params={{ id: set.id }}
                            search={{ pageId }}
                          >
                            ຈັດການ
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="h-24 text-center text-muted-foreground"
                    >
                      ຍັງບໍ່ມີຊຸດສິນຄ້າ
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </Main>
    </>
  );
}

export function ProductSetDetailPage() {
  const { has } = usePermissions();
  const canManage = has("live:manage");
  const { id } = useParams({ from: "/app/product-sets/$id" });
  const search = useSearch({ from: "/app/product-sets/$id" });
  const nav = useNavigate({ from: "/app/product-sets/$id" });
  const client = useQueryClient();
  const [name, setName] = useState("");
  const [price, setPrice] = useState<PriceInput>(emptyPrice);
  const [selected, setSelected] = useState<string[]>([]);
  const [tab, setTab] = useState("selected");
  const [managingQueue, setManagingQueue] = useState(false);
  const [queueSearch, setQueueSearch] = useState("");
  const [queueReadiness, setQueueReadiness] = useState("all");
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogSearchInput, setCatalogSearchInput] = useState("");
  const [catalogReadiness, setCatalogReadiness] = useState("all");
  const [catalogSelection, setCatalogSelection] = useState<AdminCatalogItem[]>(
    [],
  );
  const [removalSelection, setRemovalSelection] = useState<Set<string>>(
    () => new Set(),
  );
  const [dirty, setDirty] = useState(false);
  const [priceChanged, setPriceChanged] = useState(false);
  const [reviewedPriceFingerprint, setReviewedPriceFingerprint] = useState<
    string | null
  >(null);
  const [priceDialog, setPriceDialog] = useState<{
    previewItems: AdminCatalogItem[];
    commitItems: AdminCatalogItem[];
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const savingRef = useRef(false);
  const deletingRef = useRef(false);
  const debouncedCatalogSearch = useDebounceCallback(
    (next: string) => setCatalogSearch(next.trim()),
    300,
  );
  const pageId = search.pageId;
  const isNew = id === "new";
  const sets = useQuery({
    queryKey: listKey(pageId ?? ""),
    queryFn: () => productSetApi.list(pageId ?? ""),
    enabled: Boolean(pageId),
  });
  const saved = isNew ? undefined : sets.data?.find((set) => set.id === id);
  useEffect(() => {
    if (!saved) return;
    setName(saved.name);
    setPrice(saved.items[0]?.price ?? emptyPrice);
    setSelected(saved.items.map((item) => item.variantId));
    setCatalogSelection([]);
    setRemovalSelection(new Set());
    setDirty(false);
    setPriceChanged(false);
    setReviewedPriceFingerprint(null);
    setPriceDialog(null);
  }, [saved]);
  useEffect(() => {
    if (!isNew) return;
    setName("");
    setPrice(emptyPrice);
    setSelected([]);
    setCatalogSelection([]);
    setRemovalSelection(new Set());
    setDirty(false);
    setPriceChanged(false);
    setReviewedPriceFingerprint(null);
    setPriceDialog(null);
  }, [isNew, pageId]);
  const catalog = useInfiniteQuery({
    queryKey: [
      "cf-product-set-catalog",
      pageId,
      catalogSearch,
      catalogReadiness,
    ],
    queryFn: ({ pageParam, signal }) =>
      productSetApi.catalog(
        {
          pageId: pageId ?? "",
          q: catalogSearch || undefined,
          limit: CATALOG_PAGE_LIMIT,
          offset: pageParam,
          groupByProduct: true,
          readiness:
            catalogReadiness === "all"
              ? undefined
              : (catalogReadiness as "ready" | "zero" | "preorder"),
        },
        signal,
      ),
    enabled: Boolean(pageId) && canManage,
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce(
        (sum, page) => sum + (page.products?.length ?? 0),
        0,
      );
      return loaded < lastPage.total ? loaded : undefined;
    },
  });
  const selectedCatalog = useQuery({
    queryKey: ["cf-product-set-catalog-exact", pageId, selected],
    queryFn: ({ signal }) =>
      productSetApi.catalog(
        {
          pageId: pageId ?? "",
          variantIds: selected,
          limit: 100,
          groupByProduct: true,
        },
        signal,
      ),
    enabled: Boolean(pageId) && selected.length > 0,
  });
  const catalogProducts = useMemo(
    () => catalog.data?.pages.flatMap((page) => page.products ?? []) ?? [],
    [catalog.data],
  );
  const selectedIds = useMemo(() => new Set(selected), [selected]);
  const selectedCatalogItems = selectedCatalog.data?.items ?? [];
  const orderedSelectedCatalogItems = useMemo(() => {
    const byId = new Map(
      selectedCatalogItems.map((item) => [item.variantId, item]),
    );
    return selected.flatMap((variantId) => {
      const item = byId.get(variantId);
      return item ? [item] : [];
    });
  }, [selected, selectedCatalogItems]);
  const selectedCatalogProductRows = selectedCatalog.data?.products ?? [];
  const selectedCatalogProducts = useMemo(
    () =>
      selectedCatalogProductRows
        .map((product) => ({
          ...product,
          variants: product.variants.filter((item) =>
            selectedIds.has(item.variantId),
          ),
        }))
        .filter((product) => product.variants.length > 0)
        .sort(
          (left, right) =>
            Math.min(
              ...left.variants.map((item) => selected.indexOf(item.variantId)),
            ) -
            Math.min(
              ...right.variants.map((item) => selected.indexOf(item.variantId)),
            ),
        ),
    [selectedCatalogProductRows, selected, selectedIds],
  );
  const filteredSelectedCatalogProducts = useMemo(() => {
    const query = queueSearch.trim().normalize("NFKC").toLowerCase();
    return selectedCatalogProducts.filter((product) => {
      const matchesSearch =
        !query ||
        product.name.normalize("NFKC").toLowerCase().includes(query) ||
        product.variants.some((item) =>
          item.sku.normalize("NFKC").toLowerCase().includes(query),
        );
      const matchesReadiness =
        queueReadiness === "all" ||
        (queueReadiness === "ready" &&
          product.variants.some(
            (item) => item.isPreorder || item.availableToAdd > 0,
          )) ||
        (queueReadiness === "zero" &&
          product.variants.every(
            (item) => !item.isPreorder && item.availableToAdd <= 0,
          )) ||
        (queueReadiness === "preorder" &&
          product.variants.some((item) => item.isPreorder));
      return matchesSearch && matchesReadiness;
    });
  }, [queueReadiness, queueSearch, selectedCatalogProducts]);
  const availableCatalogProducts = useMemo(
    () =>
      catalogProducts
        .map((product) => ({
          ...product,
          variants: product.variants.filter(
            (item) => !selectedIds.has(item.variantId),
          ),
        }))
        .filter((product) => product.variants.length > 0),
    [catalogProducts, selectedIds],
  );
  const unavailableSelectedCount = Math.max(
    0,
    selected.length - selectedCatalogItems.length,
  );
  const resolvedSelectedIds = useMemo(
    () => new Set(selectedCatalogItems.map((item) => item.variantId)),
    [selectedCatalogItems],
  );
  const unavailableSelectedIds = useMemo(
    () => selected.filter((variantId) => !resolvedSelectedIds.has(variantId)),
    [resolvedSelectedIds, selected],
  );
  const removeVariantIds = (variantIds: Iterable<string>) => {
    const removing = new Set(variantIds);
    if (removing.size === 0) return;
    setSelected((current) =>
      current.filter((variantId) => !removing.has(variantId)),
    );
    setRemovalSelection((current) => {
      const next = new Set(current);
      for (const variantId of removing) next.delete(variantId);
      return next;
    });
    setDirty(true);
    setReviewedPriceFingerprint(null);
  };
  const updateRemovalSelection = (variantIds: string[], checked: boolean) => {
    setRemovalSelection((current) =>
      updateCatalogSelection(current, variantIds, checked),
    );
  };
  const updatePendingCatalogSelection = (
    items: AdminCatalogItem[],
    checked: boolean,
  ) => {
    const currentIds = new Set(catalogSelection.map((item) => item.variantId));
    const next = updateCatalogSelection(
      currentIds,
      items.map((item) => item.variantId),
      checked,
    );
    if (next.size + selected.length > 100) {
      toast.error("ເລືອກໄດ້ສູງສຸດ 100 SKU ຕໍ່ຊຸດ");
      return;
    }
    setCatalogSelection(
      [
        ...new Map(
          [...catalogSelection, ...items].map((item) => [item.variantId, item]),
        ).values(),
      ].filter((item) => next.has(item.variantId)),
    );
  };
  const commitCatalogItems = (items: AdminCatalogItem[]) => {
    const itemIds = new Set(items.map((item) => item.variantId));
    const additions = items.filter((item) => !selectedIds.has(item.variantId));
    if (selected.length + additions.length > 100) {
      toast.error("ເລືອກໄດ້ສູງສຸດ 100 SKU ຕໍ່ຊຸດ");
      return;
    }
    setSelected((current) => [
      ...current,
      ...additions.map((item) => item.variantId),
    ]);
    if (additions.length > 0) setDirty(true);
    setCatalogSelection((current) =>
      current.filter((item) => !itemIds.has(item.variantId)),
    );
  };
  const openCatalogPriceDialog = (items: AdminCatalogItem[]) => {
    const additions = items.filter((item) => !selectedIds.has(item.variantId));
    if (selected.length + additions.length > 100) {
      toast.error("ເລືອກໄດ້ສູງສຸດ 100 SKU ຕໍ່ຊຸດ");
      return;
    }
    if (additions.length === 0) return;
    setPriceDialog({ previewItems: additions, commitItems: additions });
  };
  const reviewCurrentPrices = () => {
    if (unavailableSelectedIds.length > 0) {
      toast.error("ມີ SKU ທີ່ບໍ່ພ້ອມໃຫ້ກວດລາຄາ. ນຳລາຍການນັ້ນອອກກ່ອນ");
      return;
    }
    if (orderedSelectedCatalogItems.length === 0) return;
    setPriceDialog({
      previewItems: orderedSelectedCatalogItems,
      commitItems: [],
    });
  };
  const moveProductGroup = (productId: string, nextPosition: number) => {
    const currentGroups = selectedCatalogProducts.map((product) => ({
      productId: product.productId,
      variantIds: product.variants.map((item) => item.variantId),
    }));
    const fromIndex = currentGroups.findIndex(
      (group) => group.productId === productId,
    );
    if (fromIndex < 0) return;
    const targetIndex = Math.max(
      0,
      Math.min(currentGroups.length - 1, nextPosition - 1),
    );
    if (fromIndex === targetIndex) return;
    const nextGroups = [...currentGroups];
    const [moving] = nextGroups.splice(fromIndex, 1);
    if (!moving) return;
    nextGroups.splice(targetIndex, 0, moving);
    const known = new Set(currentGroups.flatMap((group) => group.variantIds));
    setSelected((current) => [
      ...nextGroups.flatMap((group) => group.variantIds),
      ...current.filter((variantId) => !known.has(variantId)),
    ]);
    setDirty(true);
  };
  const save = async () => {
    if (savingRef.current || deletingRef.current) return;
    if (!pageId || !name.trim() || !selected.length)
      return toast.error("ກະລຸນາຕັ້ງຊື່ ແລະ ເລືອກ SKU");
    if (id !== "new" && sets.isSuccess && !saved)
      return toast.error("ບໍ່ພົບຊຸດສິນຄ້ານີ້");
    if (
      (price.mode === "sale" ||
        price.mode === "percent" ||
        price.mode === "fixed") &&
      price.value == null
    )
      return toast.error("ກະລຸນາລະບຸລາຄາ");
    if (priceChanged) {
      if (unavailableSelectedIds.length > 0)
        return toast.error("ມີ SKU ທີ່ບໍ່ພ້ອມໃຫ້ກວດລາຄາ. ນຳລາຍການນັ້ນອອກກ່ອນ");
      const currentFingerprint = productSetPriceFingerprint(
        price,
        orderedSelectedCatalogItems,
      );
      if (reviewedPriceFingerprint !== currentFingerprint) {
        reviewCurrentPrices();
        return toast.error("ກວດລາຄາກ່ອນບັນທຶກຊຸດ");
      }
    }
    const input = {
      pageId,
      name: name.trim(),
      items: selected.map((variantId) => ({ variantId, price })),
    };
    savingRef.current = true;
    setSaving(true);
    try {
      const result =
        id !== "new"
          ? await productSetApi.update(id, input)
          : await productSetApi.create(input);
      await client.invalidateQueries({ queryKey: listKey(pageId) });
      setDirty(false);
      setPriceChanged(false);
      setReviewedPriceFingerprint(null);
      toast.success("ບັນທຶກຊຸດສິນຄ້າແລ້ວ");
      nav({
        to: "/app/product-sets/$id",
        params: { id: result.id },
        search: { pageId },
      });
    } catch {
      toast.error("ບັນທຶກຊຸດສິນຄ້າບໍ່ສຳເລັດ");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  const removeSet = async () => {
    if (!saved || deletingRef.current || savingRef.current) return;
    const accepted = await confirm({
      title: `ລົບຊຸດ ${saved.name}?`,
      description: "ບໍ່ກະທົບ Feed ຫຼື Live ທີ່ເຄີຍເພີ່ມສິນຄ້າຈາກຊຸດນີ້",
      actionText: "ລົບຊຸດ",
      ActionProps: { variant: "destructive" },
    });
    if (!accepted) return;
    deletingRef.current = true;
    setDeleting(true);
    try {
      await productSetApi.remove(saved.id);
      await client.invalidateQueries({ queryKey: listKey(pageId ?? "") });
      toast.success("ລົບຊຸດສິນຄ້າແລ້ວ");
      nav({ to: "/app/product-sets", search: { pageId } });
    } catch {
      toast.error("ລົບຊຸດສິນຄ້າບໍ່ສຳເລັດ");
    } finally {
      deletingRef.current = false;
      setDeleting(false);
    }
  };
  return (
    <>
      <Header />
      <Main>
        <div className="mx-auto max-w-5xl space-y-5">
          <Link
            to="/app/product-sets"
            search={{ pageId }}
            className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
          >
            <RiArrowLeftLine size={18} /> ກັບໄປລາຍການຊຸດສິນຄ້າ
          </Link>
          <div>
            <h1 className="font-semibold text-2xl">
              {isNew ? "ສ້າງຊຸດສິນຄ້າ" : "ລາຍລະອຽດຊຸດສິນຄ້າ"}
            </h1>
            <p className="text-muted-foreground text-sm">
              {isNew
                ? "ບັນທຶກໄວ້ກ່ອນ ແລ້ວເລືອກໃຊ້ໃນ Feed ຫຼື Live ໄດ້"
                : "ເບິ່ງ ແລະ ແກ້ໄຂສິນຄ້າໃນຊຸດນີ້"}
            </p>
          </div>
          <PagePicker
            value={pageId}
            onChange={(next) => nav({ search: { pageId: next } })}
          />
          {!pageId ? (
            <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
              ເລືອກເພຈກ່ອນ
            </div>
          ) : (
            <>
              <div className="rounded-xl border bg-card">
                <div className="border-b p-4">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <div className="w-full max-w-xl space-y-1">
                      <span className="text-muted-foreground text-xs">
                        ຊື່ຊຸດສິນຄ້າ
                      </span>
                      <Input
                        disabled={!canManage}
                        value={name}
                        onChange={(event) => {
                          setName(event.target.value);
                          setDirty(true);
                        }}
                        placeholder="ຊື່ຊຸດສິນຄ້າ"
                        maxLength={120}
                      />
                    </div>
                    {saved && canManage ? (
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={() => void removeSet()}
                        disabled={deleting || saving}
                      >
                        <RiDeleteBinLine size={18} />
                        {deleting ? "ກຳລັງລົບ..." : "ລົບຊຸດ"}
                      </Button>
                    ) : null}
                  </div>
                </div>
                <Tabs value={tab} onValueChange={setTab} className="min-w-0">
                  <TabsList className="flex h-auto w-full flex-wrap justify-start rounded-none border-b bg-transparent p-0">
                    <TabsTrigger className="min-h-11" value="selected">
                      ສິນຄ້າທີ່ເພີ່ມແລ້ວ ({selected.length})
                    </TabsTrigger>
                    {canManage ? (
                      <TabsTrigger className="min-h-11" value="catalog">
                        ສິນຄ້າທີ່ຍັງບໍ່ໄດ້ເພີ່ມ
                      </TabsTrigger>
                    ) : null}
                  </TabsList>
                  <TabsContent value="selected" className="m-0">
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
                      <div>
                        <h2 className="font-medium">ສິນຄ້າໃນຊຸດ</h2>
                        <p className="text-muted-foreground text-xs">
                          {selectedCatalogProducts.length} ສິນຄ້າ ·{" "}
                          {selected.length} SKU
                        </p>
                        <p
                          className={
                            dirty
                              ? "text-amber-700 text-xs dark:text-amber-400"
                              : "text-muted-foreground text-xs"
                          }
                          aria-live="polite"
                        >
                          {dirty ? "ມີການປ່ຽນແປງທີ່ຍັງບໍ່ບັນທຶກ" : "ບັນທຶກແລ້ວ"}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {canManage ? (
                          <>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={reviewCurrentPrices}
                              disabled={
                                !selected.length ||
                                selectedCatalog.isLoading ||
                                saving ||
                                deleting
                              }
                            >
                              <RiCheckLine size={18} />
                              ກວດລາຄາກ່ອນບັນທຶກ
                            </Button>
                            <Button
                              variant={managingQueue ? "secondary" : "outline"}
                              aria-pressed={managingQueue}
                              onClick={() =>
                                setManagingQueue((value) => !value)
                              }
                            >
                              {managingQueue ? (
                                <RiCheckLine size={18} />
                              ) : (
                                <RiDragMove2Line size={18} />
                              )}
                              {managingQueue ? "ຈັດລຳດັບແລ້ວ" : "ຈັດລຳດັບ"}
                            </Button>
                            <Button
                              onClick={() => void save()}
                              disabled={!selected.length || saving || deleting}
                            >
                              <RiSaveLine size={18} />
                              {saving
                                ? "ກຳລັງບັນທຶກ..."
                                : isNew
                                  ? "ບັນທຶກຊຸດ"
                                  : "ບັນທຶກການແກ້ໄຂ"}
                            </Button>
                          </>
                        ) : (
                          <Badge variant="secondary">ອ່ານຢ່າງດຽວ</Badge>
                        )}
                      </div>
                    </div>
                    {canManage && managingQueue ? (
                      <p className="border-b px-4 py-3 text-muted-foreground text-xs">
                        ລາກ ຫຼື ໃສ່ລຳດັບເພື່ອຈັດຄິວ. ລຳດັບນີ້ຈະຖືກນຳໄປໃຊ້ຕອນເພີ່ມຊຸດເຂົ້າ Feed ຫຼື
                        Live.
                      </p>
                    ) : null}
                    <Collapsible className="border-b p-4">
                      <div className="flex gap-2">
                        <Input
                          aria-label="ຄົ້ນຫາສິນຄ້າໃນຊຸດ"
                          placeholder="ຄົ້ນຫາຊື່ ຫຼື SKU…"
                          value={queueSearch}
                          onChange={(event) =>
                            setQueueSearch(event.target.value)
                          }
                          className="min-h-11 min-w-0 flex-1"
                        />
                        <CollapsibleTrigger asChild>
                          <Button
                            variant="outline"
                            className="min-h-11 shrink-0"
                          >
                            ຕົວກອງ
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent className="pt-3">
                        <WorkspaceSelect
                          label="ຄວາມພ້ອມ"
                          value={queueReadiness}
                          onChange={setQueueReadiness}
                          items={[
                            ["all", "ທັງໝົດ"],
                            ["ready", "ພ້ອມຂາຍ"],
                            ["zero", "ສະຕັອກ 0"],
                            ["preorder", "Preorder"],
                          ]}
                        />
                      </CollapsibleContent>
                    </Collapsible>
                    {!selectedCatalog.isLoading &&
                    unavailableSelectedCount > 0 ? (
                      <p className="border-b bg-muted/30 p-3 text-muted-foreground text-xs">
                        {unavailableSelectedCount} SKU ບໍ່ຢູ່ໃນລາຍການຄັງປັດຈຸບັນ
                        ແຕ່ຍັງຖືກເກັບໄວ້ໃນຊຸດ
                      </p>
                    ) : null}
                    {selected.length === 0 ? (
                      <div className="flex flex-col items-center justify-center gap-2 p-12 text-center text-muted-foreground">
                        <p className="text-sm">ຍັງບໍ່ມີສິນຄ້າໃນຊຸດ</p>
                        {canManage ? (
                          <Button
                            variant="outline"
                            onClick={() => setTab("catalog")}
                          >
                            ເພີ່ມສິນຄ້າ
                          </Button>
                        ) : null}
                      </div>
                    ) : selectedCatalog.isLoading ? (
                      <div className="p-8 text-center text-muted-foreground text-sm">
                        ກຳລັງໂຫຼດສິນຄ້າໃນຊຸດ...
                      </div>
                    ) : filteredSelectedCatalogProducts.length === 0 &&
                      unavailableSelectedIds.length === 0 ? (
                      <div className="p-8 text-center text-muted-foreground text-sm">
                        ບໍ່ພົບສິນຄ້າຕາມຄຳຄົ້ນ ຫຼື ຕົວກອງ
                      </div>
                    ) : (
                      <div
                        className={`grid max-h-[42rem] gap-2 overflow-auto overscroll-contain p-3 ${
                          managingQueue || removalSelection.size > 0
                            ? "grid-cols-1"
                            : "grid-cols-2 2xl:grid-cols-3"
                        }`}
                      >
                        {filteredSelectedCatalogProducts.map((product) => {
                          const position =
                            selectedCatalogProducts.findIndex(
                              (candidate) =>
                                candidate.productId === product.productId,
                            ) + 1;
                          const groupVariantIds = product.variants.map(
                            (item) => item.variantId,
                          );
                          const actions: WorkspaceProductCardAction[] =
                            canManage
                              ? [
                                  {
                                    id: "remove-product",
                                    label: "ນຳສິນຄ້າອອກຈາກຊຸດ",
                                    icon: (
                                      <RiDeleteBinLine
                                        className="size-4"
                                        aria-hidden
                                      />
                                    ),
                                    destructive: true,
                                    onSelect: () =>
                                      removeVariantIds(groupVariantIds),
                                  },
                                ]
                              : [];
                          return (
                            <WorkspaceProductCard
                              key={product.productId}
                              productId={product.productId}
                              name={product.name}
                              imageUrl={product.variants[0]?.imageUrl}
                              subtitle={
                                <>
                                  ລຳດັບ {position} ·{" "}
                                  {product.variants
                                    .slice(0, 3)
                                    .map(
                                      (item) =>
                                        item.defaultCfCode ??
                                        item.suggestedCfCode ??
                                        item.sku,
                                    )
                                    .join(" · ")}
                                  {product.variants.length > 3
                                    ? ` · +${product.variants.length - 3}`
                                    : ""}
                                </>
                              }
                              price={`${product.variants.length} SKU`}
                              variants={product.variants.map((item) => ({
                                id: item.variantId,
                                selectionLabel: `ເລືອກ ${item.sku} ເພື່ອນຳອອກ`,
                                content: (
                                  <>
                                    <strong className="text-primary">
                                      {item.defaultCfCode ??
                                        item.suggestedCfCode ??
                                        item.sku}
                                    </strong>
                                    <span className="block truncate text-muted-foreground text-xs">
                                      {item.sku} ·{" "}
                                      {item.isAvailable === false
                                        ? "ບໍ່ພ້ອມໃຊ້ງານ · ສາມາດນຳອອກໄດ້"
                                        : item.isPreorder
                                          ? "Preorder"
                                          : item.availableToAdd <= 0
                                            ? "ສະຕັອກ 0"
                                            : `ຄົງເຫຼືອ ${item.availableToAdd}`}
                                    </span>
                                  </>
                                ),
                                mobilePrice: kip(Number(item.price)),
                                desktopPrice: (
                                  <strong className="text-sm tabular-nums">
                                    {kip(Number(item.price))}
                                  </strong>
                                ),
                                actions: canManage
                                  ? [
                                      {
                                        id: "remove",
                                        label: "ນຳອອກ",
                                        icon: (
                                          <RiDeleteBinLine
                                            className="size-4"
                                            aria-hidden
                                          />
                                        ),
                                        destructive: true,
                                        onSelect: () =>
                                          removeVariantIds([item.variantId]),
                                      },
                                    ]
                                  : [],
                              }))}
                              selectedVariantIds={
                                canManage ? removalSelection : undefined
                              }
                              onSelectionChange={
                                canManage ? updateRemovalSelection : undefined
                              }
                              selectionDisabled={
                                !canManage || saving || deleting
                              }
                              actions={actions}
                              draggable={
                                canManage &&
                                managingQueue &&
                                !saving &&
                                !deleting
                              }
                              onDragStart={(event) =>
                                event.dataTransfer.setData(
                                  "text/plain",
                                  product.productId,
                                )
                              }
                              onDrop={(event) => {
                                event.preventDefault();
                                const sourceId =
                                  event.dataTransfer.getData("text/plain");
                                if (
                                  managingQueue &&
                                  sourceId &&
                                  sourceId !== product.productId
                                )
                                  moveProductGroup(sourceId, position);
                              }}
                              management={
                                canManage && managingQueue ? (
                                  <div className="flex flex-wrap items-center gap-2 border-t px-3 py-2">
                                    <RiDragMove2Line
                                      className="size-5 text-muted-foreground"
                                      aria-label="ຈັດລຳດັບ"
                                    />
                                    <Button
                                      type="button"
                                      variant="outline"
                                      className="size-11"
                                      disabled={position === 1}
                                      aria-label={`ເລື່ອນ ${product.name} ຂຶ້ນ`}
                                      onClick={() =>
                                        moveProductGroup(
                                          product.productId,
                                          position - 1,
                                        )
                                      }
                                    >
                                      <RiArrowUpLine size={18} />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      className="size-11"
                                      disabled={
                                        position ===
                                        selectedCatalogProducts.length
                                      }
                                      aria-label={`ເລື່ອນ ${product.name} ລົງ`}
                                      onClick={() =>
                                        moveProductGroup(
                                          product.productId,
                                          position + 1,
                                        )
                                      }
                                    >
                                      <RiArrowDownLine size={18} />
                                    </Button>
                                    <label
                                      htmlFor={`product-set-position-${product.productId}`}
                                      className="ml-auto flex min-h-11 items-center gap-2 text-sm"
                                    >
                                      <span>ລຳດັບ</span>
                                      <Input
                                        id={`product-set-position-${product.productId}`}
                                        key={position}
                                        type="number"
                                        min={1}
                                        max={selectedCatalogProducts.length}
                                        defaultValue={position}
                                        className="min-h-11 w-20"
                                        onBlur={(event) =>
                                          moveProductGroup(
                                            product.productId,
                                            Number(event.target.value),
                                          )
                                        }
                                      />
                                    </label>
                                  </div>
                                ) : null
                              }
                            />
                          );
                        })}
                        {unavailableSelectedIds.map((variantId) => (
                          <div
                            key={variantId}
                            className="flex min-h-14 items-center justify-between gap-3 rounded-lg border border-dashed px-3 py-2"
                          >
                            <div className="min-w-0">
                              <p className="font-medium text-sm">
                                ສິນຄ້າບໍ່ພ້ອມໃຊ້ງານ
                              </p>
                              <p className="text-muted-foreground text-xs">
                                ລຳດັບ {selected.indexOf(variantId) + 1} ·
                                ສາມາດນຳອອກຈາກຊຸດໄດ້
                              </p>
                            </div>
                            {canManage ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => removeVariantIds([variantId])}
                              >
                                <RiDeleteBinLine size={18} /> ນຳອອກ
                              </Button>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    )}
                    {canManage && removalSelection.size > 0 ? (
                      <section
                        className="sticky bottom-0 z-20 flex flex-wrap items-center gap-2 border-t bg-background p-3 shadow-lg"
                        aria-label="ລາຍການທີ່ເລືອກນຳອອກ"
                      >
                        <p className="mr-auto text-sm">
                          ເລືອກ {removalSelection.size} SKU
                        </p>
                        <Button
                          type="button"
                          variant="destructive"
                          disabled={saving || deleting}
                          onClick={() => removeVariantIds(removalSelection)}
                        >
                          <RiDeleteBinLine size={18} />
                          ນຳອອກທີ່ເລືອກ ({removalSelection.size})
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setRemovalSelection(new Set())}
                        >
                          ລ້າງທີ່ເລືອກ
                        </Button>
                      </section>
                    ) : null}
                  </TabsContent>
                  {canManage ? (
                    <TabsContent value="catalog" className="m-0">
                      <div className="space-y-4 border-b p-4">
                        <div>
                          <h2 className="font-semibold text-lg">
                            ເລືອກສິນຄ້າເຂົ້າຊຸດ
                          </h2>
                          <p className="mt-1 text-muted-foreground text-sm">
                            ເພີ່ມທັງສິນຄ້າໄດ້ໃນຄັ້ງດຽວ ຫຼືເປີດຕົວເລືອກເພື່ອເລືອກແຕ່ບາງ SKU.
                          </p>
                        </div>
                        <Input
                          placeholder="ຄົ້ນຫາສິນຄ້າ / SKU / ສະແກນ barcode"
                          value={catalogSearchInput}
                          onChange={(event) => {
                            setCatalogSearchInput(event.target.value);
                            debouncedCatalogSearch(event.target.value);
                          }}
                          className="min-h-11"
                        />
                        <WorkspaceSelect
                          label="ຄວາມພ້ອມ"
                          value={catalogReadiness}
                          onChange={(value) => {
                            setCatalogReadiness(value);
                            setCatalogSelection([]);
                          }}
                          items={[
                            ["all", "ທັງໝົດ ລວມສະຕັອກ 0"],
                            ["ready", "ພ້ອມຂາຍ"],
                            ["zero", "ສະຕັອກ 0"],
                            ["preorder", "Preorder"],
                          ]}
                        />
                        <section className="space-y-2 rounded-lg border bg-muted/20 p-3">
                          <div>
                            <h3 className="font-medium text-sm">
                              ຕັ້ງລາຄາ / ສ່ວນຫຼຸດ ກ່ອນເພີ່ມ
                            </h3>
                            <p className="text-muted-foreground text-xs">
                              ຄ່ານີ້ຈະໃຊ້ກັບທຸກ SKU ໃນຊຸດນີ້
                            </p>
                          </div>
                          <div className="grid gap-2 sm:grid-cols-[minmax(0,14rem)_minmax(0,12rem)_auto] sm:items-end">
                            <WorkspaceSelect
                              label="ລາຄາທີ່ໃຊ້"
                              value={price.mode}
                              onChange={(mode) => {
                                setPrice(
                                  mode === "inherit" || mode === "none"
                                    ? { mode }
                                    : {
                                        mode: mode as
                                          | "sale"
                                          | "percent"
                                          | "fixed",
                                        value: price.value ?? 0,
                                      },
                                );
                                setDirty(true);
                                setPriceChanged(true);
                                setReviewedPriceFingerprint(null);
                              }}
                              items={[
                                ["inherit", "ໃຊ້ລາຄາສິນຄ້າ"],
                                ["sale", "ກອກລາຄາຂາຍຈິງ"],
                                ["percent", "ຫຼຸດ %"],
                                ["fixed", "ຫຼຸດເປັນກີບ"],
                                ["none", "ບໍ່ຫຼຸດລາຄາ"],
                              ]}
                            />
                            {price.mode !== "inherit" &&
                            price.mode !== "none" ? (
                              <Input
                                type="number"
                                min={0}
                                max={price.mode === "percent" ? 100 : 999999999}
                                value={price.value ?? ""}
                                onChange={(event) => {
                                  setPrice({
                                    ...price,
                                    value:
                                      event.target.value === ""
                                        ? undefined
                                        : Number(event.target.value),
                                  });
                                  setDirty(true);
                                  setPriceChanged(true);
                                  setReviewedPriceFingerprint(null);
                                }}
                                className="min-h-11"
                              />
                            ) : null}
                            <Button
                              type="button"
                              variant="secondary"
                              className="min-h-11"
                              disabled={
                                catalogSelection.length === 0 ||
                                saving ||
                                deleting
                              }
                              onClick={() =>
                                openCatalogPriceDialog(catalogSelection)
                              }
                            >
                              <RiCheckLine size={18} />
                              ກວດລາຄາທີ່ເລືອກ ({catalogSelection.length})
                            </Button>
                          </div>
                        </section>
                        <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
                          <p className="text-muted-foreground text-xs">
                            ເພີ່ມ SKU ແລ້ວ ກົດບັນທຶກເພື່ອໃຊ້ຊຸດນີ້ຕໍ່ໄປ
                          </p>
                          <Button
                            type="button"
                            onClick={() => void save()}
                            disabled={!selected.length || saving || deleting}
                          >
                            <RiSaveLine size={18} />
                            {saving ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກຊຸດ"}
                          </Button>
                        </div>
                      </div>
                      <QueryState
                        result={catalog}
                        isFetching={
                          catalog.isFetching && !catalog.isFetchingNextPage
                        }
                      >
                        {availableCatalogProducts.length > 0 ? (
                          <div className="space-y-3 p-3">
                            <WorkspaceCatalogCards
                              products={availableCatalogProducts}
                              selectedItems={catalogSelection}
                              disabled={saving || deleting}
                              hasNextPage={Boolean(catalog.hasNextPage)}
                              isFetchingNextPage={catalog.isFetchingNextPage}
                              onLoadMore={() => void catalog.fetchNextPage()}
                              onSelectionChange={updatePendingCatalogSelection}
                              onCommit={openCatalogPriceDialog}
                              onClear={() => setCatalogSelection([])}
                              allowWholeProductCommitWithSelection
                              reviewBeforeCommit
                              filtered={Boolean(
                                catalogSearch || catalogReadiness !== "all",
                              )}
                            />
                          </div>
                        ) : (
                          <p className="p-8 text-center text-muted-foreground text-sm">
                            ບໍ່ພົບສິນຄ້າທີ່ຍັງບໍ່ໄດ້ເພີ່ມ
                          </p>
                        )}
                      </QueryState>
                    </TabsContent>
                  ) : null}
                </Tabs>
              </div>
              <div className="flex flex-wrap gap-2 border-t pt-4">
                <Button
                  variant="outline"
                  onClick={() => nav({ to: "/app/feeds" })}
                >
                  <RiNewspaperLine size={18} /> ໄປເລືອກ Feed ເພື່ອໃຊ້
                </Button>
                <Button
                  variant="outline"
                  onClick={() => nav({ to: "/app/live" })}
                >
                  <RiLiveLine size={18} /> ໄປເລືອກ Live ເພື່ອໃຊ້
                </Button>
              </div>
              {canManage && priceDialog ? (
                <WorkspaceCatalogPriceDialog
                  items={priceDialog.previewItems}
                  initialPrice={price}
                  busy={saving || deleting}
                  onClose={() => setPriceDialog(null)}
                  onConfirm={(nextPrice) => {
                    const nextFingerprint = productSetPriceFingerprint(
                      nextPrice,
                      priceDialog.previewItems,
                    );
                    if (JSON.stringify(nextPrice) !== JSON.stringify(price)) {
                      setPriceChanged(true);
                      setDirty(true);
                    }
                    setPrice(nextPrice);
                    setReviewedPriceFingerprint(nextFingerprint);
                    commitCatalogItems(priceDialog.commitItems);
                    setPriceDialog(null);
                  }}
                />
              ) : null}
            </>
          )}
        </div>
      </Main>
    </>
  );
}
